import { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { useConfig } from '../config.js';
import { permissionNote, currentPushContext } from '../push-help.js';
import { CoralMark } from './CoralMark.jsx';
import { formatVolume, sizeBucketsInUnit, volumeLabel } from '../units.js';
import {
  buildDeltaLabel,
  volumeInputText,
  volumeInputToGallons,
  buildCopy,
} from '../tank-build.js';
import { fillDateBounds, validFillDate } from '../fill-date.js';
import {
  ONBOARDING_STEPS,
  previousStep,
  welcomeName,
  withOneRetry,
  saveErrorMessage,
  sharedWelcome,
} from '../onboarding-steps.js';

// Post-signup onboarding — 9 logical steps across 6 phases:
//   welcome → intent → (water → subtype → size → age) → photo → first-read
//   → notify
//
// Every step PATCHes progress server-side so a user can bail mid-flow and
// resume on another device. POST /api/onboarding/complete (fired at the end
// of first-read) creates the tank row, moves the uploaded photo onto the
// tank, and sets users.onboarding_completed_at. The final `notify` step runs
// AFTER completion — it sets up notification channels and is purely optional,
// so it can never block the keeper from reaching the app. After that the
// user never sees this.
//
// Chip copy and headlines come from the server-side `onboarding_taxonomy`
// + `onboarding_copy` rows so admin can edit them without a redeploy (see
// server/db/seeds DEFAULT_ONBOARDING_*). The step list for the progress
// dots is the flow's own (onboarding-steps.js).

export function OnboardingFlow({ user, onComplete }) {
  const config = useConfig();
  const taxonomy = config?.onboarding?.taxonomy;
  const copy = config?.onboarding?.copy;
  const STEPS = ONBOARDING_STEPS;
  const [progress, setProgress] = useState(null); // null = loading
  const [hasPhoto, setHasPhoto] = useState(false);
  const [step, setStep] = useState('welcome');
  const [error, setError] = useState('');
  const [finishing, setFinishing] = useState(false);
  const [sharedTanks, setSharedTanks] = useState(0);
  const [shared, setShared] = useState([]);
  const finishingRef = useRef(false);
  const patchSeq = useRef(0);

  // Initial load — resume from where they left off.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api('/api/onboarding');
        if (!alive) return;
        if (r.completed) {
          onComplete();
          return;
        }
        setProgress(r.progress || {});
        setHasPhoto(!!r.has_photo);
        setSharedTanks(Number(r.shared_tanks) || 0);
        setShared(Array.isArray(r.shared) ? r.shared : []);
        if (r.progress?.current_step && STEPS.includes(r.progress.current_step)) {
          setStep(r.progress.current_step);
        }
      } catch (e) {
        setError(e.message || 'Could not load onboarding.');
        setProgress({});
      }
    })();
    return () => {
      alive = false;
    };
  }, [onComplete]);

  const advance = (next) => {
    setStep(next);
    // Fire-and-forget — server stores current_step so resume works cross-device.
    api('/api/onboarding', { method: 'PATCH', body: { current_step: next } }).catch(() => {});
  };

  // The answer shows at once and stays on screen if the save fails, and a
  // save that hit a deploy (502) or a dropped connection is tried once more.
  // Only the newest save's reply is applied, so a slow reply to an earlier
  // keystroke in the volume box cannot undo a later one.
  const patch = async (partial, opts = {}) => {
    const seq = ++patchSeq.current;
    setError('');
    setProgress((p) => ({ ...(p || {}), ...partial }));
    try {
      const r = await withOneRetry(() =>
        api('/api/onboarding', { method: 'PATCH', body: partial })
      );
      if (seq === patchSeq.current) setProgress(r.progress);
      if (opts.next) advance(opts.next);
      return r.progress;
    } catch (e) {
      if (seq === patchSeq.current) setError(saveErrorMessage(e));
      throw e;
    }
  };

  if (progress === null) {
    return (
      <OnboardingFrame step={null} STEPS={STEPS}>
        <Loading />
      </OnboardingFrame>
    );
  }

  // Water-type branch drives subtype options. Some chips ("unsure", a
  // saltwater "Haven't decided") are only offered to users with a
  // particular intent — encoded as `only_intent` on the taxonomy entry,
  // applied below at the per-step level.

  return (
    <OnboardingFrame
      step={step}
      STEPS={STEPS}
      onBack={() => {
        const prev = previousStep(step, progress, STEPS);
        if (prev) advance(prev);
      }}
    >
      {error && (
        <div className="ob-error" role="alert">
          {error}
        </div>
      )}
      {step === 'welcome' && (
        <WelcomeStep
          user={user}
          copy={copy?.welcome}
          onNext={() => advance('intent')}
          sharedTanks={sharedTanks}
          shared={shared}
          onSkipToShared={async () => {
            try {
              await api('/api/onboarding/skip-to-shared', { method: 'POST' });
              onComplete();
            } catch (e) {
              setError(e.message || 'Could not open the shared tank.');
            }
          }}
        />
      )}
      {step === 'intent' && (
        <IntentStep
          h1={copy?.intent?.h1}
          options={taxonomy?.intents || []}
          value={progress.intent}
          onPick={(v) => patch({ intent: v }, { next: 'water' })}
        />
      )}
      {step === 'water' && (
        <WaterTypeStep
          h1={copy?.water?.h1}
          options={(taxonomy?.water_types || []).filter(
            (o) => !o.only_intent || progress.intent === o.only_intent
          )}
          value={progress.water_type}
          onPick={(v) =>
            patch({ water_type: v, subtype: null }, { next: v === 'unsure' ? 'size' : 'subtype' })
          }
        />
      )}
      {step === 'subtype' && (
        <SubtypeStep
          h1={
            progress.water_type === 'freshwater'
              ? copy?.subtype?.freshwater_h1
              : copy?.subtype?.saltwater_h1
          }
          options={subtypeOptionsFor(taxonomy, progress.water_type, progress.intent)}
          value={progress.subtype}
          onPick={(v) => patch({ subtype: v }, { next: 'size' })}
          onSkip={() => advance('size')}
        />
      )}
      {step === 'size' && (
        <SizeStep
          h1={copy?.size?.h1}
          options={taxonomy?.sizes || []}
          value={progress.size}
          gallons={progress.size_gallons}
          volumeUnit={progress.display_volume_unit || 'gal'}
          tempUnit={progress.display_temp_unit || 'F'}
          onPick={(bucket) => {
            const meta = (taxonomy?.sizes || []).find((s) => s.k === bucket);
            const prefill = progress.size_gallons || (meta?.midpoint ? String(meta.midpoint) : '');
            patch({ size: bucket, size_gallons: prefill });
          }}
          onGallons={(g) => patch({ size_gallons: g })}
          onVolumeUnit={(u) => patch({ display_volume_unit: u })}
          onTempUnit={(u) => patch({ display_temp_unit: u })}
          onContinue={() => advance('age')}
          onSkip={() => advance('age')}
        />
      )}
      {step === 'age' && (
        <AgeStep
          h1={copy?.age?.h1}
          options={taxonomy?.ages || []}
          value={progress.age}
          filledOn={progress.filled_on}
          onPick={(v) => patch({ age: v, filled_on: null }, { next: 'photo' })}
          onFilled={(d) => patch({ age: '<1mo', filled_on: d }, { next: 'photo' })}
          onSkip={() => advance('photo')}
        />
      )}
      {step === 'photo' && (
        <PhotoStep
          copy={copy?.photo}
          hasPhoto={hasPhoto}
          setHasPhoto={setHasPhoto}
          onNext={() => advance('build')}
        />
      )}
      {step === 'build' && (
        <BuildStep
          progress={progress}
          onSave={(buildFields) =>
            patch(
              {
                build_sump_gal: buildFields.sump_gal,
                build_rock_density: buildFields.rock_density,
                build_sand_depth: buildFields.sand_depth,
              },
              { next: 'first-read' }
            )
          }
          onSkip={() => advance('first-read')}
        />
      )}
      {step === 'first-read' && (
        <SenseiReadStep
          loadingMsg={copy?.loading_msg}
          ctaLabel={copy?.first_read_cta}
          progress={progress}
          busy={finishing}
          onDone={async () => {
            // A second tap while the first is in flight made a second tank.
            if (finishingRef.current) return;
            finishingRef.current = true;
            setFinishing(true);
            try {
              // Claim-guarded on the server: a retry after a 502 that did
              // finish answers 409, handled below.
              await withOneRetry(() => api('/api/onboarding/complete', { method: 'POST' }));
              // Tank is created and onboarding is marked complete; the notify
              // step that follows is a post-completion bonus and can be left
              // any time (refresh/close drops straight into the app).
              advance('notify');
            } catch (e) {
              // 409: an earlier tap (or another tab) already finished it.
              if (e.status === 409) {
                advance('notify');
              } else {
                setError(saveErrorMessage(e, 'Could not finish. Try again?'));
                finishingRef.current = false;
                setFinishing(false);
              }
            }
          }}
        />
      )}
      {step === 'notify' && <NotifyStep user={user} onDone={onComplete} />}
    </OnboardingFrame>
  );
}

// Subtype list = the water-type's sub-array, with `only_intent`-gated
// entries (e.g. "Haven't decided") filtered out unless the user's intent
// matches.
function subtypeOptionsFor(taxonomy, waterType, intent) {
  const all = taxonomy?.subtypes?.[waterType === 'freshwater' ? 'freshwater' : 'saltwater'] || [];
  return all.filter((o) => !o.only_intent || intent === o.only_intent);
}

// ─── Frame + progress ───────────────────────────────

function OnboardingFrame({ step, STEPS, children, onBack }) {
  // Force light theme for the duration of this flow. Save + restore the
  // caller's choice so leaving the flow lands the user back in whatever
  // they had before.
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'light');
    return () => {
      if (prev) document.documentElement.setAttribute('data-theme', prev);
    };
  }, []);

  const idx = step ? STEPS.indexOf(step) : -1;
  const showBack = idx > 0 && step !== 'first-read' && step !== 'notify';

  return (
    <div className="ob-root">
      <header className="ob-header">
        {showBack ? (
          <button type="button" className="ob-back" onClick={onBack} aria-label="Go back">
            ←
          </button>
        ) : (
          <div style={{ width: 32 }} />
        )}
        <div className="ob-progress" aria-hidden="true">
          {STEPS.map((s, i) => (
            <span
              key={s}
              className={`ob-dot ${i === idx ? 'active' : ''} ${i < idx ? 'done' : ''}`}
            />
          ))}
        </div>
        <div style={{ width: 32 }} />
      </header>
      <main className="ob-main">
        <div className="ob-step fade-in" key={step}>
          {children}
        </div>
      </main>
    </div>
  );
}

function Loading() {
  return (
    <div className="ob-loading">
      <CoralMark size={40} />
    </div>
  );
}

// ─── Shared chip button ─────────────────────────────

function Chips({ options, value, onPick, columns = 1 }) {
  return (
    <div className="ob-chips" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {options.map((o) => (
        <button
          key={o.k}
          type="button"
          className={`ob-chip ${value === o.k ? 'active' : ''}`}
          onClick={() => onPick(o.k)}
        >
          <span className="ob-chip-label">{o.label}</span>
          {o.sub && <span className="ob-chip-sub">{o.sub}</span>}
        </button>
      ))}
    </div>
  );
}

function SkipLink({ onClick }) {
  return (
    <button type="button" className="ob-skip" onClick={onClick}>
      Skip for now
    </button>
  );
}

// ─── Steps ──────────────────────────────────────────

function WelcomeStep({ user, copy, onNext, sharedTanks = 0, shared = [], onSkipToShared }) {
  const name = welcomeName(user?.email);
  const nameSuffix = name ? `, ${name}` : '';
  const h2 = (copy?.h2_template || "Let's meet yours{{name_suffix}}.").replace(
    '{{name_suffix}}',
    nameSuffix
  );
  // An invitee came for someone's tank: say whose and which, instead of
  // "Let's meet yours" above a button to someone else's tank.
  const invite = sharedWelcome(shared, sharedTanks);
  return (
    <div className="ob-welcome">
      <CoralMark className="ob-kanji" size={64} />
      <h1 className="ob-h1">{copy?.h1 || 'Every tank has a story.'}</h1>
      <h2 className="ob-h2" style={invite ? { overflowWrap: 'anywhere' } : undefined}>
        {invite ? invite.heading : h2}
      </h2>
      {invite && (
        <p className="ob-p">
          You can go straight to {sharedTanks > 1 ? 'them' : 'it'}, or set up a tank of your own.
        </p>
      )}
      {invite && (
        <button type="button" className="ob-primary" onClick={onSkipToShared}>
          {invite.cta}
        </button>
      )}
      <button type="button" className={invite ? 'ob-skip' : 'ob-primary'} onClick={onNext}>
        {invite ? 'Set up my own tank' : copy?.cta || 'Begin'}
      </button>
    </div>
  );
}

function IntentStep({ h1, options, value, onPick }) {
  return (
    <>
      <h1 className="ob-h1">{h1 || 'What brings you to TankSensei?'}</h1>
      <Chips options={options} value={value} onPick={onPick} />
    </>
  );
}

function WaterTypeStep({ h1, options, value, onPick }) {
  return (
    <>
      <h1 className="ob-h1">{h1 || 'Is it saltwater or freshwater?'}</h1>
      <Chips options={options} value={value} onPick={onPick} />
    </>
  );
}

function SubtypeStep({ h1, options, value, onPick, onSkip }) {
  return (
    <>
      <h1 className="ob-h1">{h1 || "What's the style of tank?"}</h1>
      <Chips options={options} value={value} onPick={onPick} />
      <SkipLink onClick={onSkip} />
    </>
  );
}

function SizeStep({
  h1,
  options,
  value,
  gallons,
  volumeUnit,
  tempUnit,
  onPick,
  onGallons,
  onVolumeUnit,
  onTempUnit,
  onContinue,
  onSkip,
}) {
  const typed = Number(gallons);
  const valid = Number.isFinite(typed) && typed >= 1 && typed <= 10_000;
  const canContinue = valid || !!value;
  const isLitres = volumeUnit === 'L';
  // Display the typed gallons in the user's chosen unit. The progress
  // blob still stores gallons (storage is canonical), so a toggle just
  // re-renders without losing the underlying value.
  const storedText =
    isLitres && Number.isFinite(typed) && typed > 0
      ? (typed * 3.785411784).toFixed(1).replace(/\.0$/, '')
      : gallons || '';
  // What the keeper typed stays in the box while they type. Re-deriving it
  // from the saved gallons blanked a litres keeper's "1" (under the 1 gal
  // floor, saved as nothing) on the way to "110", and ate a typed "11.".
  // The saved value is read again when the unit or the size bucket changes.
  const [inputValue, setInputValue] = useState(storedText);
  useEffect(() => {
    setInputValue(storedText);
  }, [volumeUnit, value]);
  const onInput = (raw) => {
    setInputValue(raw);
    const n = Number(String(raw).trim().replace(',', '.'));
    if (raw === '' || !Number.isFinite(n)) {
      onGallons('');
      return;
    }
    onGallons(String(isLitres ? n / 3.785411784 : n));
  };
  return (
    <>
      <h1 className="ob-h1">{h1 || 'How big is the tank?'}</h1>
      <Chips
        options={sizeBucketsInUnit(options, volumeUnit)}
        value={value}
        onPick={onPick}
        columns={2}
      />
      <div className="ob-exact">
        <label className="ob-exact-label font-heading" htmlFor="ob-size-exact">
          Exact volume
        </label>
        <div className="ob-exact-row">
          <input
            id="ob-size-exact"
            type="number"
            inputMode="decimal"
            min="1"
            max="10000"
            step="0.1"
            placeholder={isLitres ? 'e.g. 110' : 'e.g. 29'}
            value={inputValue}
            onChange={(e) => onInput(e.target.value)}
            className="ob-input"
          />
          <ObUnitToggle
            ariaLabel="Volume unit"
            value={volumeUnit}
            options={[
              { k: 'gal', label: 'gal' },
              { k: 'L', label: 'L' },
            ]}
            onPick={onVolumeUnit}
          />
        </div>
        <div className="ob-exact-row ob-exact-temp-row">
          <span className="ob-exact-label font-heading" style={{ flex: 1 }}>
            Temperature display
          </span>
          <ObUnitToggle
            ariaLabel="Temperature unit"
            value={tempUnit}
            options={[
              { k: 'F', label: '°F' },
              { k: 'C', label: '°C' },
            ]}
            onPick={onTempUnit}
          />
        </div>
        <p className="ob-exact-hint">
          Tapping a bucket above prefills the volume — overwrite with your real number so dose math
          is accurate from day 1. Units stay with this tank; you can change them later in Tank
          settings.
        </p>
      </div>
      <div className="ob-actions">
        <button type="button" className="ob-primary" onClick={onContinue} disabled={!canContinue}>
          Continue
        </button>
      </div>
      <SkipLink onClick={onSkip} />
    </>
  );
}

// Small inline segmented control used on the size step. Two or three
// options; the selected one gets the cyan fill. Same visual as the
// salinity unit picker in TankSettings so the keeper sees a consistent
// pattern across the app.
function ObUnitToggle({ value, options, onPick, ariaLabel }) {
  return (
    <div className="ob-unit-toggle" role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.k}
          type="button"
          className={`ob-unit-chip${value === o.k ? ' is-active' : ''}`}
          onClick={() => onPick(o.k)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function AgeStep({ h1, options, value, filledOn, onPick, onFilled, onSkip }) {
  const [askDay, setAskDay] = useState(value === '<1mo');
  const { min, max } = fillDateBounds();
  const [day, setDay] = useState(validFillDate(filledOn) || max);
  return (
    <>
      <h1 className="ob-h1">{h1 || 'When did you set it up?'}</h1>
      <Chips
        options={options}
        value={value}
        onPick={(v) => {
          // A tank under a month old: the day matters for the cycle, so ask
          // for it rather than guessing two weeks.
          if (v === '<1mo') setAskDay(true);
          else onPick(v);
        }}
      />
      {askDay && (
        <div className="ob-exact">
          <label className="ob-exact-label font-heading" htmlFor="ob-filled-on">
            When was it filled?
          </label>
          <input
            id="ob-filled-on"
            type="date"
            className="ob-input"
            min={min}
            max={max}
            value={day}
            onChange={(e) => setDay(e.target.value)}
          />
          <div className="ob-actions">
            <button
              type="button"
              className="ob-primary"
              disabled={!validFillDate(day)}
              onClick={() => onFilled(day)}
            >
              Continue
            </button>
          </div>
        </div>
      )}
      <SkipLink onClick={onSkip} />
    </>
  );
}

function PhotoStep({ copy, hasPhoto, setHasPhoto, onNext }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);

  // If we resumed with an existing upload, load it so the user sees their picture.
  useEffect(() => {
    if (hasPhoto && !previewUrl) setPreviewUrl(`/api/onboarding/photo?t=${Date.now()}`);
  }, [hasPhoto, previewUrl]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      setErr('That file is too big. 15 MB max.');
      return;
    }
    setErr('');
    setUploading(true);
    try {
      // Try the canvas for every type: Safari decodes HEIC, and an iPhone
      // must ship a small JPEG, not raw HEIC (see prepareImageUpload). A
      // browser that cannot decode it ships raw bytes; Claude Vision takes
      // only jpeg/png/webp/gif, so that photo may fall back to the no-photo
      // path during generate, which still does not block the keeper.
      let blob = file;
      let mime = file.type || 'image/jpeg';
      try {
        blob = await resizeToJpeg(file, 1600, 0.85);
        mime = 'image/jpeg';
      } catch {
        /* fall back to raw upload */
      }
      const r = await fetch('/api/onboarding/photo', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': mime },
        body: blob,
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `Upload failed (${r.status})`);
      }
      setHasPhoto(true);
      const localUrl = URL.createObjectURL(file);
      setPreviewUrl(localUrl);
    } catch (e) {
      setErr(e.message || "Couldn't upload that photo.");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    setUploading(true);
    try {
      await fetch('/api/onboarding/photo', { method: 'DELETE', credentials: 'include' });
      setHasPhoto(false);
      setPreviewUrl(null);
    } catch (e) {
      setErr(e.message || "Couldn't remove the photo.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <h1 className="ob-h1">{copy?.h1 || 'One more thing.'}</h1>
      <p className="ob-p">
        {copy?.body ||
          'Share a photo of your tank if you have one. It becomes the picture on your tank, and the start of its photo history.'}
      </p>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
        onChange={onPick}
        style={{ display: 'none' }}
      />

      {previewUrl ? (
        <div className="ob-photo-preview">
          <img src={previewUrl} alt="Your tank" />
          <button
            type="button"
            className="ob-photo-remove"
            onClick={removePhoto}
            disabled={uploading}
          >
            {uploading ? 'Working…' : 'Remove'}
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="ob-photo-upload"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading…' : 'Upload photo'}
        </button>
      )}
      {err && (
        <p role="alert" className="ob-error" style={{ marginTop: 10 }}>
          {err}
        </p>
      )}

      <div className="ob-actions">
        <button type="button" className="ob-primary" onClick={onNext} disabled={uploading}>
          {hasPhoto
            ? copy?.cta_with_photo || 'Continue'
            : copy?.cta_without_photo || 'Skip for now'}
        </button>
      </div>
    </>
  );
}

function SenseiReadStep({ progress, loadingMsg, ctaLabel, onDone, busy = false }) {
  const [message, setMessage] = useState(progress.first_message || null);
  const [typed, setTyped] = useState('');
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState('');
  const triedRef = useRef(false);

  useEffect(() => {
    if (triedRef.current || message) return;
    triedRef.current = true;
    setGenerating(true);
    api('/api/onboarding/generate', { method: 'POST' })
      .then((r) => {
        setMessage(r.message || '');
        // Cache in onboarding_progress so a refresh mid-read doesn't burn
        // another AI call.
        api('/api/onboarding', { method: 'PATCH', body: { first_message: r.message || '' } }).catch(
          () => {}
        );
      })
      .catch((e) => setErr(e.message || 'Could not compose a first read.'))
      .finally(() => setGenerating(false));
  }, [message]);

  // Typing animation — capped at 3s regardless of message length.
  useEffect(() => {
    if (!message) return;
    const total = message.length;
    const duration = Math.min(3000, total * 25);
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const pct = Math.min(1, (now - start) / duration);
      const count = Math.round(pct * total);
      setTyped(message.slice(0, count));
      if (pct < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [message]);

  const subtitle = generating
    ? progress.photo_hint || loadingMsg || 'Sensei is putting your tank together…'
    : null;

  return (
    <>
      <CoralMark className="ob-kanji small" size={32} />
      {subtitle && <p className="ob-loading-msg">{subtitle}</p>}
      {err && (
        <p role="alert" className="ob-error">
          {err}
        </p>
      )}
      {message && (
        <div className="ob-sensei-msg">
          {typed}
          {typed.length < message.length && <span className="ob-caret">|</span>}
        </div>
      )}
      {message && typed.length === message.length && (
        <div className="ob-actions">
          <button type="button" className="ob-primary" onClick={onDone} disabled={busy}>
            {busy ? 'Setting up your tank…' : ctaLabel || 'Enter TankSensei'}
          </button>
        </div>
      )}
    </>
  );
}

// ─── Notification channels (post-completion, optional) ──
//
// The tank already exists and onboarding is committed by the time we get
// here, so nothing in this step can block the keeper from the app — every
// affordance is opt-in and "Enter TankSensei" / closing the tab both land
// them inside. Email is already on (it's their account address); this step
// only OFFERS the louder channels: a browser push subscription and SMS for
// critical alerts. Mirrors the wiring in AccountPanel's DeliveryChannels so
// the two never drift: push = enableWebPush(); SMS = PUT /notifications/sms
// (the channel only sends once email_prefs.channels.sms is true, which the
// confirm flow sets via PATCH /api/auth/me).
function NotifyStep({ user, onDone }) {
  const smsOn = useConfig()?.sms?.enabled === true;
  const [pushState, setPushState] = useState('idle'); // idle | busy | on | note
  const [pushNote, setPushNote] = useState('');
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsState, setSmsState] = useState('idle'); // idle | busy | on
  const [smsNote, setSmsNote] = useState('');
  const [phone, setPhone] = useState('');
  // Email carries only what needs the keeper; these two are theirs to add.
  const [morning, setMorning] = useState(user?.email_prefs?.digest === true);
  const [news, setNews] = useState(user?.broadcast_opt_out === false);
  const [emailNote, setEmailNote] = useState('');

  // One running copy of the prefs, so the morning switch and the SMS
  // confirm below never overwrite each other with a stale blob.
  const prefsRef = useRef(
    user?.email_prefs && typeof user.email_prefs === 'object' ? user.email_prefs : {}
  );
  const saveEmailChoice = (patch) => {
    let body;
    if ('digest' in patch) {
      prefsRef.current = { ...prefsRef.current, digest: patch.digest };
      body = { email_prefs: prefsRef.current };
    } else {
      body = { product_news: patch.product_news };
    }
    return api('/api/auth/me', { method: 'PATCH', body });
  };

  const enablePush = async () => {
    setPushNote('');
    setPushState('busy');
    try {
      const { enableWebPush } = await import('../push.js');
      await enableWebPush();
      setPushState('on');
    } catch (e) {
      // webpush defaults on in prefs, so the preference is already saved;
      // only the browser subscription failed. Tell them why, don't block.
      const reason = e?.reason;
      setPushNote(
        permissionNote(reason, currentPushContext()) ||
          (reason === 'unsupported'
            ? "This browser can't do push (iOS needs the app on your Home Screen first). You'll still get email."
            : reason === 'not_configured'
              ? 'Saved. Push starts working once we finish setting it up on our side.'
              : 'Saved, but this browser did not switch push on. You can try again later in Account.')
      );
      setPushState('note');
    }
  };

  const confirmSms = async () => {
    setSmsNote('');
    setSmsState('busy');
    try {
      const r = await api('/api/notifications/sms', {
        method: 'PUT',
        body: { phone, consent: true },
      });
      if (r.consented) {
        // Flip the SMS channel on in the prefs blob so the sender uses it.
        // Merge onto whatever the user already has (defaults at this point)
        // so we don't reset their other channels.
        const cur = prefsRef.current;
        prefsRef.current = { ...cur, channels: { ...(cur.channels || {}), sms: true } };
        await api('/api/auth/me', {
          method: 'PATCH',
          body: { email_prefs: prefsRef.current },
        }).catch(() => {});
        setSmsState('on');
        setSmsNote('Texts on for critical alerts. Reply STOP to any message to opt out.');
      } else {
        setSmsState('idle');
        setSmsNote('Could not confirm — check the number.');
      }
    } catch (e) {
      setSmsState('idle');
      setSmsNote(
        e?.payload?.error === 'invalid_phone' ? 'That phone number looks off.' : 'Could not save.'
      );
    }
  };

  return (
    <div className="ob-notify">
      <h1 className="ob-h1">Stay in the loop</h1>
      <p className="ob-h2">
        When a test you log, or a probe you connect, reads outside your range, Sensei tells you.
        Choose how.
      </p>

      <div className="ob-notify-channels">
        {/* Email — already yours, just confirm where it lands. */}
        <div className="ob-notify-row">
          <div className="ob-notify-row-main">
            <span className="ob-notify-row-title">Email</span>
            <span className="ob-notify-row-sub">{user?.email || 'Your account address'}</span>
          </div>
          <span className="ob-notify-on" aria-label="On">
            On
          </span>
        </div>

        <p className="ob-notify-note" style={{ marginTop: 0 }}>
          We email you when something needs you: a reading far out of range, or a display or probe
          gone quiet. Anything else is your choice:
        </p>
        <label className="ob-notify-row" style={{ cursor: 'pointer' }}>
          <div className="ob-notify-row-main">
            <span className="ob-notify-row-title">Morning summary</span>
            <span className="ob-notify-row-sub">
              One email a day: what's off, today's tasks, what's running low.
            </span>
          </div>
          <input
            type="checkbox"
            checked={morning}
            onChange={(e) => {
              const on = e.target.checked;
              setMorning(on);
              setEmailNote('');
              saveEmailChoice({ digest: on }).catch(() => {
                prefsRef.current = { ...prefsRef.current, digest: !on };
                setMorning(!on);
                setEmailNote("Couldn't save that. Try again, or change it later in Account.");
              });
            }}
            style={{ width: 22, height: 22 }}
          />
        </label>
        <label className="ob-notify-row" style={{ cursor: 'pointer' }}>
          <div className="ob-notify-row-main">
            <span className="ob-notify-row-title">Product news</span>
            <span className="ob-notify-row-sub">
              New features and updates from the TankSensei team.
            </span>
          </div>
          <input
            type="checkbox"
            checked={news}
            onChange={(e) => {
              const on = e.target.checked;
              setNews(on);
              setEmailNote('');
              saveEmailChoice({ product_news: on }).catch(() => {
                setNews(!on);
                setEmailNote("Couldn't save that. Try again, or change it later in Account.");
              });
            }}
            style={{ width: 22, height: 22 }}
          />
        </label>

        {emailNote && <p className="ob-notify-note">{emailNote}</p>}

        {/* Web push — one tap, browser permission prompt. */}
        <div className="ob-notify-row">
          <div className="ob-notify-row-main">
            <span className="ob-notify-row-title">Push notifications</span>
            <span className="ob-notify-row-sub">
              Alerts on this device, even when TankSensei is closed.
            </span>
          </div>
          {pushState === 'on' ? (
            <span className="ob-notify-on" aria-label="On">
              On
            </span>
          ) : (
            <button
              type="button"
              className="ob-notify-action"
              disabled={pushState === 'busy'}
              onClick={enablePush}
            >
              {pushState === 'busy' ? '…' : 'Turn on'}
            </button>
          )}
        </div>
        {pushNote && <p className="ob-notify-note">{pushNote}</p>}

        {/* Texts are hidden until the admin `sms` setting is switched on. */}
        {smsOn && (
          <>
            {/* SMS — optional, needs a number + explicit consent. */}
            <div className="ob-notify-row">
              <div className="ob-notify-row-main">
                <span className="ob-notify-row-title">Text message</span>
                <span className="ob-notify-row-sub">
                  Critical alerts only: dangerous swings and a display going offline.
                </span>
              </div>
              {smsState === 'on' ? (
                <span className="ob-notify-on" aria-label="On">
                  On
                </span>
              ) : (
                <button
                  type="button"
                  className="ob-notify-action"
                  onClick={() => setSmsOpen((v) => !v)}
                >
                  Add phone
                </button>
              )}
            </div>
            {smsOpen && smsState !== 'on' && (
              <div className="ob-notify-sms">
                <input
                  type="tel"
                  value={phone}
                  placeholder="+1 555 123 4567"
                  onChange={(e) => setPhone(e.target.value)}
                />
                <button
                  type="button"
                  className="ob-notify-action"
                  disabled={smsState === 'busy' || !phone.trim()}
                  onClick={confirmSms}
                >
                  {smsState === 'busy' ? '…' : 'Confirm'}
                </button>
              </div>
            )}
            {smsNote && <p className="ob-notify-note">{smsNote}</p>}
          </>
        )}
      </div>

      <button type="button" className="ob-primary" onClick={onDone}>
        Enter TankSensei
      </button>
      <SkipLink onClick={onDone} />
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────

function resizeToJpeg(file, maxDim, quality) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const scale = Math.min(1, maxDim / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Couldn't encode image"))),
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Couldn't read image"));
    };
    img.src = url;
  });
}

// Tank Build — optional onboarding step. Defaults to medium rock + shallow
// sand + no sump (matches createTank's seed). Filling it in here means the
// dose calculator is accurate on day 1; skipping means it falls back to
// label gallons until the keeper sets it up in TankSettings later.
function BuildStep({ progress, onSave, onSkip }) {
  const config = useConfig();
  const taxonomy = config?.onboarding?.taxonomy;
  // The keeper's own unit from the size step: a litres keeper saw
  // "15.3g · -5g vs your 19.812903926861132g label · Sump (gallons)".
  const unit = progress?.display_volume_unit === 'L' ? 'L' : 'gal';
  const [sumpText, setSumpText] = useState(() => volumeInputText(progress?.build_sump_gal, unit));
  const sumpGal = volumeInputToGallons(sumpText, unit);
  const [rock, setRock] = useState(progress?.build_rock_density || 'medium');
  const [sand, setSand] = useState(progress?.build_sand_depth || 'shallow');
  const copy = buildCopy(unit, { freshwater: progress?.water_type === 'freshwater' });

  // Live preview — same constants the server uses in tank-volume.js. We
  // re-compute here client-side to give immediate feedback as the user
  // toggles the chips, without an extra round trip. Display volume comes
  // from the typed exact gallons if the user provided them, otherwise
  // the size-band midpoint they picked one step earlier.
  const typedExact = Number(progress?.size_gallons);
  const exact =
    Number.isFinite(typedExact) && typedExact >= 1 && typedExact <= 10000 ? typedExact : null;
  const sizeBand = (taxonomy?.sizes || []).find((s) => s.k === progress?.size);
  const display = exact ?? (Number(sizeBand?.midpoint) || null);
  const ROCK_FRAC = { none: 0, light: 0.1, medium: 0.18, heavy: 0.27 };
  const SAND_FRAC = { bare: 0, shallow: 0.05, deep: 0.12 };
  const sumpContrib = Math.max(0, Number(sumpGal) || 0) * 0.6;
  const working =
    display && display > 0
      ? Math.round(
          (display * (1 - (ROCK_FRAC[rock] ?? 0.18) - (SAND_FRAC[sand] ?? 0.05)) + sumpContrib) * 10
        ) / 10
      : null;

  const Chip = ({ field, value, label, sub }) => {
    const active = (field === 'rock' ? rock : sand) === value;
    const onPick = () => (field === 'rock' ? setRock(value) : setSand(value));
    return (
      <button
        type="button"
        onClick={onPick}
        className={`ob-build-chip ${active ? 'active' : ''}`}
        aria-pressed={active}
      >
        <span className="ob-build-chip-label">{label}</span>
        {sub && <span className="ob-build-chip-sub">{sub}</span>}
      </button>
    );
  };

  return (
    <div className="ob-step ob-build slide-in">
      <h2 className="ob-step-h1 font-heading">Tank build (optional)</h2>
      <p className="ob-step-hint">
        {copy.lead} These three knobs let Sensei work out the real working volume — so when the dose
        calculator says "add 18 mL," it's actually 18 mL for <em>your</em> water.
      </p>

      {working != null && (
        <div className="ob-build-result">
          <div className="ob-build-result-label">Working water volume</div>
          <div className="ob-build-result-value">
            {formatVolume(working, unit).split(' ')[0]}
            <span className="ob-build-result-unit">{volumeLabel(unit)}</span>
          </div>
          <div className="ob-build-result-delta">{buildDeltaLabel(working, display, unit)}</div>
        </div>
      )}

      <label className="ob-build-field">
        <span>Sump ({unit === 'L' ? 'litres' : 'gallons'})</span>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          value={sumpText}
          onChange={(e) => setSumpText(e.target.value)}
          placeholder="0 (no sump)"
        />
        <small>We count 60% of this — sumps never fill to the top.</small>
      </label>

      <div className="ob-build-field">
        <span>Rockscape</span>
        <div className="ob-build-chips">
          <Chip field="rock" value="none" label="None" sub="bare bottom" />
          <Chip field="rock" value="light" label="Light" sub="−10%" />
          <Chip field="rock" value="medium" label="Medium" sub="−18%" />
          <Chip field="rock" value="heavy" label="Heavy" sub="−27%" />
        </div>
      </div>

      <div className="ob-build-field">
        <span>Sand bed</span>
        <div className="ob-build-chips">
          <Chip field="sand" value="bare" label="Bare" sub="0%" />
          <Chip field="sand" value="shallow" label="Shallow" sub={copy.sandShallow} />
          <Chip field="sand" value="deep" label="Deep" sub={copy.sandDeep} />
        </div>
      </div>

      <div className="ob-build-actions">
        <button type="button" className="ob-build-skip" onClick={onSkip}>
          Skip for now
        </button>
        <button
          type="button"
          className="ob-build-save"
          onClick={() =>
            onSave({
              sump_gal: sumpGal,
              rock_density: rock,
              sand_depth: sand,
            })
          }
        >
          Save and continue
        </button>
      </div>
      <p className="ob-build-footer">You can always fine-tune this later in Tank settings.</p>
    </div>
  );
}
